import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { effect, inject, DOCUMENT } from '@angular/core';
import { ThemeService } from '@theme/data-access';
import { LocalStorageFacade } from '@application/localstorage';
import { debounceTime, fromEvent } from 'rxjs';
import { RouterStore } from '@route/data-access';


export interface IUiStore {
  userClickEventTarget: any | null;
  theme: {
    dark: boolean;
  };
  screen: {
    breakpoint?:
    | 'Unknown'
    | 'XSmall'
    | 'Small'
    | 'Medium'
    | 'Large'
    | 'XLarge'
    | string;
    innerWidth?: number | null;
    innerHeight?: number | null;
    orientation?: 'landscape' | 'portrait' | string | null;
  };
  opacityScreen?: boolean;
  hideMaxWidth?: boolean;
}

export const UiInitialStore: IUiStore = {
  userClickEventTarget: null,
  theme: { dark: false },
  screen: {
    breakpoint: 'Unknown',
    innerWidth: null,
    innerHeight: null,
    orientation: null,
  },
  opacityScreen: false,
  hideMaxWidth: false,
};

const displayNameMap = new Map([
  [Breakpoints.XSmall, 'XSmall'],
  [Breakpoints.Small, 'Small'],
  [Breakpoints.Medium, 'Medium'],
  [Breakpoints.Large, 'Large'],
  [Breakpoints.XLarge, 'XLarge'],
]);

export const UiStore = signalStore(
  { providedIn: 'root' },
  withState(UiInitialStore),
  withMethods((
    state,
    themeService = inject(ThemeService),
    localStorageService = inject(LocalStorageFacade)
  ) => ({
    bodyClick: (payload: any) => patchState(state, { userClickEventTarget: payload }),
    toggleUiTheme: (theme: string) => {
      const isDark = theme === 'dark';
      if (isDark === state.theme.dark()) return;
      themeService.toggleUiTheme();
      localStorageService.setItem('theme', isDark ? 'dark' : 'light');
      patchState(state, { theme: { dark: isDark } });
    },
    changeMaxWidth: (show) => patchState(state, { hideMaxWidth: show ?? !!state.hideMaxWidth() }),
    updateUiTheme: (payload: string) => patchState(state, { theme: { dark: payload === 'dark' } }),
    updateOpacityScreen: (payload: boolean) => patchState(state, { opacityScreen: payload }),
  })
  ),
  withHooks({
    onInit: (
      state,
      themeService = inject(ThemeService),
      localStorageService = inject(LocalStorageFacade),
      breakpointObserver = inject(BreakpointObserver),
      routerStore = inject(RouterStore),
      document = inject(DOCUMENT)
    ) => {
      let theme = localStorageService.getItem('theme');
      if (!theme) {
        theme = document.documentElement.classList.contains('dark') ? 'dark' : 'light';
        localStorageService.setItem('theme', theme)
      }
      if (theme === 'dark' || theme === 'light') {
        patchState(state, { theme: { dark: theme === 'dark' } });
      }
      breakpointObserver
        .observe([
          Breakpoints.XSmall,
          Breakpoints.Small,
          Breakpoints.Medium,
          Breakpoints.Large,
          Breakpoints.XLarge,
        ])
        .subscribe((result) => {
          for (const query of Object.keys(result.breakpoints)) {
            if (result.breakpoints[query]) {
              patchState(state, {
                screen: {
                  ...state.screen(),
                  breakpoint: displayNameMap.get(query) ?? 'Unknown'
                }
              });
            }
          }
        }
        );
      fromEvent(window, 'resize')
        .pipe(debounceTime(100))
        .subscribe((data: any) => {
          const height = JSON.parse(JSON.stringify(data?.target?.innerHeight));
          const width = JSON.parse(JSON.stringify(data?.target?.innerWidth));
          patchState(state, { screen: { ...state.screen(), innerHeight: height, innerWidth: width } }
          );
        }
        );
      fromEvent(window, 'orientationchange').subscribe(() => {
        window.matchMedia('(orientation: portrait)').matches
          ? patchState(state, { screen: { ...state.screen(), orientation: 'portrait' } })
          : patchState(state, { screen: { ...state.screen(), orientation: 'landscape' } });
      });
      patchState(state, { screen: { ...state.screen(), innerHeight: window?.innerHeight, innerWidth: window?.innerWidth } })
      effect(() => {
        const route = routerStore.getCurrentRoute();
        if (route) {
          if (route?.includes('profile')) {
            document.documentElement.style.setProperty(
              '--dx-theme-background-dark',
              '#171717'
            );
            document.documentElement.style.setProperty(
              '--dx-theme-background-light',
              '#f9fafb'
            );
            return;
          }
          document.documentElement.style.setProperty(
            '--dx-theme-background-light',
            '#ffffff'
          );
          document.documentElement.style.setProperty(
            '--dx-theme-background-dark',
            'rgb(30 33 36)'
          );
        }
      })
    }
  }),
  withComputed((state) => ({
    
    isMobile: () => {
      return state.screen().breakpoint === 'Small' || state.screen().breakpoint === 'XSmall';
    },
  }))
);
